import type { Components, Theme } from '@mui/material/styles'

/**
 * ## The two `primary.contrastText` below are CORRECT — do not "fix" them
 *
 * Nine theme files in this directory defaulted an element to
 * `primary.contrastText`, and in eight of them that was wrong: `contrastText` is the
 * colour legible ON the primary colour, i.e. on a primary-FILLED surface, and a
 * checkbox / radio / link / list row / tab / form label is not on one. This file is
 * one of the two exceptions, because the same rule that sets `contrastText` also
 * sets an OPAQUE fill two lines away: `backgroundColor: '#9333ea'`.
 *
 * Measured, not argued. `#9333ea` has relative luminance 0.153, so:
 *
 * | label | ratio on `#9333ea` |
 * |---|---|
 * | `contrastText` -> `#fff` (primary is black) | **5.17:1** |
 * | `text.primary` -> `#000000` | 4.06:1 |
 *
 * `contrastText` is the BETTER of the two here and tracks the tenant's primary the
 * way it is meant to — a tenant whose `primary_color` is light gets a dark label.
 * Replacing it with `text.primary` would lower contrast, not raise it.
 *
 * ## Neither live call site reads it anyway
 *
 * `<Chip>` appears twice in `apps/app` — `ScanStatusChip` and `StatusBadge` — and
 * both spread `statusChipSx`, whose `color` lands LAST in the emitted rule and wins.
 * Dumped live on the certificates list, the declarations in source order were:
 *
 *   `color:#000000` -> `background-color:rgba(0,0,0,0.08)` -> `color:#fff` (variant)
 *   -> `background-color:#9333ea` -> `color:#fff` (root) -> `background-color:#F4F4F5`
 *   -> `color:#27272A`
 *
 * Rendered: `rgb(39,39,42)` on `rgb(244,244,245)` = **13.55:1**. The "Invalid scan"
 * chip on the public certificate page measured `rgb(127,29,29)` on `rgb(254,226,226)`
 * = **8.20:1**. So the theme's white is unreachable in practice as well as correct
 * in principle.
 *
 * (The hardcoded `#9333ea` is a separate oddity — a Vera-ish purple with no
 * relationship to `primary` — but it is a fill, which is all this note is about.)
 */
export const MuiChip: Components<Theme>['MuiChip'] = {
  defaultProps: {
    size: 'small',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      fontFamily: theme.typography.fontFamily,
      fontWeight: 400,
      padding: '5px 8px',
      color: theme.palette.primary.contrastText,
      border: `1px solid ${theme.palette.grey[600]}`,
      backgroundColor: '#9333ea',
      'span.MuiChip-label': {
        fontFamily: theme.typography.fontFamily,
        textTransform: 'capitalize',
        fontSize: 12,
        lineHeight: 1.43,
        paddingLeft: '8px',
        paddingRight: '8px',
      },
      '&.checkIcon': {
        svg: {
          width: '24px',
          height: '24px',
        },
      },
    }),
    avatarSmall: ({ theme }) => ({
      marginRight: '4px',
      '&.circleIcon': {
        width: '7px',
        marginRight: '7px',
        '&.active': {
          color: theme.palette.success.main,
        },
        '&.stolen': {
          color: theme.palette.error.main,
        },
        '&.returned': {
          color: theme.palette.grey[300],
        },
        '&.transfer_canceled,': {
          color: theme.palette.grey[200],
        },
      },
    }),
  },
  variants: [
    {
      props: { size: 'small' },
      style: {
        height: 24,
        fontSize: 12,
        lineHeight: 1.5,
      },
    },
    {
      props: { size: 'medium' },
      style: {
        height: 24,
        '.MuiChip-label': {
          paddingLeft: 8,
          paddingRight: 8,
        },
      },
    },
    {
      props: { variant: 'outlined', color: 'default' },
      style: ({ theme }) => ({
        background: `${theme.palette.grey[50]}`,
      }),
    },

    {
      props: { variant: 'filled', color: 'default' },
      style: ({ theme }) => ({
        color: theme.palette.primary.contrastText,
      }),
    },
  ],
}
